import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    Phone,
    Visibility,
    VisibilityOff,
    ArrowBack,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../api';
import TermsAndConditionsDialog from './TermsAndConditionsDialog';

const AuthPage = ({ onLoginSuccess, initialMobile, onBack, signupBusinessId: signupBusinessIdProp }) => {
    const { t } = useTranslation();
    // Prefill mobile from the landing page's inline vendor field (initialMobile)
    // or a shared bill link (?mobile=...), taking the last 10 digits so values
    // stored with a country code still match.
    const [mobile, setMobile] = useState(() => {
        const raw = initialMobile || new URLSearchParams(window.location.search).get('mobile') || '';
        const digits = raw.replace(/\D/g, '');
        return digits.slice(-10);
    });
    // When a customer signs up from a vendor's store (prop) or a shared bill link
    // (?business_id=), capture which vendor brought them in (stored as
    // f_registered_business_id / f_source on the new user).
    const signupBusinessId = (() => {
        if (Number.isFinite(signupBusinessIdProp)) return signupBusinessIdProp;
        const n = parseInt(new URLSearchParams(window.location.search).get('business_id'), 10);
        return Number.isFinite(n) ? n : null;
    })();
    // When the number is prefilled (from the landing page's inline field or a bill
    // link), skip the redundant mobile-entry screen and auto-advance to password/OTP.
    const prefilled = (initialMobile || new URLSearchParams(window.location.search).get('mobile') || '')
        .replace(/\D/g, '').slice(-10).length === 10;
    const [autoAdvancing, setAutoAdvancing] = useState(prefilled);
    const [step, setStep] = useState('mobile'); // mobile, password, otp, set-password
    const [isNewUser, setIsNewUser] = useState(false);
    const [, setHasPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [name, setName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [otpChannel, setOtpChannel] = useState('sms');
    const [otpSendCount, setOtpSendCount] = useState(0);
    const [captchaRequired, setCaptchaRequired] = useState(false);
    const [captchaQuestion, setCaptchaQuestion] = useState('');
    const [captchaExpected, setCaptchaExpected] = useState(null);
    const [captchaInput, setCaptchaInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [wrongPasswordAttempts, setWrongPasswordAttempts] = useState(0);
    const [, setOtpSent] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);

    useEffect(() => {
        // Don't auto-clear errors - let them persist until user takes action
        // This allows error messages to remain visible when returning from password step
    }, [step]);

    useEffect(() => {
        // Auto-run the mobile check once when arriving with a prefilled number.
        if (autoAdvancing && mobile.length === 10) {
            handleMobileSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatMobile = (value) => {
        // Remove non-digits, cap at 10
        const cleaned = value.replace(/\D/g, '');
        return cleaned.slice(0, 10);
    };

    const handleMobileSubmit = async () => {
        if (mobile.length !== 10) {
            setError('Please enter a valid 10-digit mobile number');
            return;
        }

        setLoading(true);
        setError('');  // Clear previous errors when submitting
        setSuccess('');

        try {
            const response = await authAPI.checkMobile(mobile);
            const data = response.data;

            setHasPassword(data.has_password);
            setIsNewUser(!data.exists);

            if (data.has_password) {
                setStep('password');
                setSuccess('Please enter your 6-digit password');
            } else {
                // Need to send OTP
                setStep('otp-channel');
                setSuccess(data.message);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to check mobile number');
        } finally {
            setLoading(false);
            setAutoAdvancing(false);
        }
    };

    const generateCaptcha = () => {
        const a = Math.floor(Math.random() * 10) + 1;
        const b = Math.floor(Math.random() * 10) + 1;
        setCaptchaQuestion(`${a} + ${b}`);
        setCaptchaExpected(a + b);
        setCaptchaInput('');
    };

    const handleSendOTP = async () => {
        if (captchaRequired) {
            if (parseInt(captchaInput, 10) !== captchaExpected) {
                setError('Incorrect answer. Please try again.');
                generateCaptcha();
                return;
            }
            setCaptchaRequired(false);
            setCaptchaInput('');
        }

        setLoading(true);
        setError('');

        try {
            await authAPI.sendOTP(mobile, otpChannel);
            const newCount = otpSendCount + 1;
            setOtpSendCount(newCount);
            setOtpSent(true);
            setStep('otp-verify');
            setSuccess('OTP sent successfully via SMS');
            if (newCount >= 3) {
                generateCaptcha();
                setCaptchaRequired(true);
            }
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to send OTP';
            setError(detail);
            // If rate limited, don't allow resend attempts via captcha bypass
            if (err.response?.status === 429) {
                setCaptchaRequired(false);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTPAndSetPassword = async () => {
        if (otp.length !== 6) {
            setError('Please enter the 6-digit OTP');
            return;
        }

        if (newPassword.length !== 6 || !/^\d+$/.test(newPassword)) {
            setError('Password must be exactly 6 digits');
            return;
        }



        setLoading(true);
        setError('');

        try {
        const response = await authAPI.verifyOTPAndSetPassword(mobile, otp, newPassword, name.trim() || undefined, signupBusinessId);
            const data = response.data;

            // Store token
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('user_id', data.user_id);
            localStorage.setItem('mobile', data.mobile);
            if (data.name) localStorage.setItem('user_name', data.name);

            setSuccess('Login successful!');
            setTimeout(() => {
                onLoginSuccess(data);
            }, 500);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to verify OTP or set password');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordLogin = async () => {
        if (password.length !== 6 || !/^\d+$/.test(password)) {
            setError('Password must be exactly 6 digits');
            return;
        }

        setLoading(true);
        setError('');  // Clear previous errors when attempting login
        setSuccess('');

        try {
            const deviceInfo = navigator.userAgent;
            const response = await authAPI.loginWithPassword(mobile, password, deviceInfo);
            const data = response.data;

            // Store token
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('user_id', data.user_id);
            localStorage.setItem('mobile', data.mobile);
            if (data.name) localStorage.setItem('user_name', data.name);

            setSuccess('Login successful!');
            setTimeout(() => {
                onLoginSuccess(data);
            }, 500);
        } catch (err) {
            const status = err.response?.status;
            if (status === 423) {
                setWrongPasswordAttempts(3);
                setError('Account locked after 3 failed attempts. Reset your password using OTP.');
            } else if (status === 401) {
                setWrongPasswordAttempts(prev => {
                    const next = prev + 1;
                    if (next >= 3) setError('Account locked. Reset your password using OTP.');
                    else setError(`Incorrect PIN. ${3 - next} attempt${3 - next === 1 ? '' : 's'} remaining.`);
                    return next;
                });
            } else {
                setError(err.response?.data?.detail || 'Login failed. Please try again.');
            }
            setPassword('');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = () => {
        setStep('otp-channel');
        setPassword('');
        setError('');
        setWrongPasswordAttempts(0);
        setSuccess('Let\'s verify your identity to reset your password');
    };

    const handleBack = () => {
        if (step === 'password') {
            // Going back from password - keep mobile number and error message
            setStep('mobile');
            setPassword('');
            // Don't clear mobile, error, or success
        } else if (step === 'otp-channel') {
            setStep('mobile');
            setMobile('');
            setPassword('');
            setOtp('');
            setName('');
            setNewPassword('');
            setConfirmPassword('');
            setOtpSendCount(0);
            setCaptchaRequired(false);
            setCaptchaInput('');
        } else if (step === 'otp-verify') {
            setStep('otp-channel');
            setOtp('');
        }
    };

    const handleKeyPress = (e, callback) => {
        if (e.key === 'Enter') {
            callback();
        }
    };

    return (
        <Container maxWidth="sm" sx={{
            px: { xs: 2, sm: 3 },
            pt: 'calc(env(safe-area-inset-top, 0px) + 8px)',
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            mt: { xs: 0, sm: 4, md: 8 },
        }}>
            {onBack && (
                <Box sx={{ mb: 1 }}>
                    <IconButton onClick={onBack} sx={{ ml: -1 }} aria-label="Back">
                        <ArrowBack />
                    </IconButton>
                </Box>
            )}
            <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2 }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 4 } }}>
                    <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
                        vendora88
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Order & Billing Platform for Delivery Businesses
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* While auto-advancing from a prefilled number, show a loader
                    instead of the redundant mobile-entry form. */}
                {step === 'mobile' && autoAdvancing && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Step 1: Mobile Number */}
                {step === 'mobile' && !autoAdvancing && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('auth.loginSignup')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('auth.enterMobile')}
                        </Typography>

                        <TextField
                            fullWidth
                            label={t('auth.mobileLabel')}
                            value={mobile}
                            onChange={(e) => setMobile(formatMobile(e.target.value))}
                            onKeyPress={(e) => handleKeyPress(e, handleMobileSubmit)}
                            placeholder={t('auth.mobilePlaceholder')}
                            type="tel"
                            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Phone sx={{ mr: 0.5, fontSize: 18 }} />
                                        <Typography variant="body1" fontWeight="bold" sx={{ mr: 0.5 }}>+91</Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleMobileSubmit}
                            disabled={loading || mobile.length !== 10}
                        >
                            {loading ? <CircularProgress size={24} /> : t('auth.continue')}
                        </Button>
                    </Box>
                )}

                {/* Step 2: Password Login */}
                {step === 'password' && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('auth.enterPassword')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('auth.passwordFor', { mobile })}
                        </Typography>

                        <TextField
                            fullWidth
                            label={t('auth.passwordLabel')}
                            value={password}
                            onChange={(e) => setPassword(formatMobile(e.target.value).slice(0, 6))}
                            onKeyPress={(e) => handleKeyPress(e, handlePasswordLogin)}
                            type={showPassword ? 'text' : 'password'}
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />

                        {/* Prominent OTP reset option shown after 3 wrong attempts */}
                        {wrongPasswordAttempts >= 3 && (
                            <Alert
                                severity="warning"
                                sx={{ mb: 2 }}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        variant="outlined"
                                        onClick={handleForgotPassword}
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Reset via OTP
                                    </Button>
                                }
                            >
                                Forgot your PIN? Reset it using OTP sent to your mobile.
                            </Alert>
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handlePasswordLogin}
                            disabled={loading || password.length !== 6 || wrongPasswordAttempts >= 3}
                            sx={{ mb: 1 }}
                        >
                            {loading ? <CircularProgress size={24} /> : t('auth.login')}
                        </Button>

                        {wrongPasswordAttempts < 3 && (
                            <Button
                                fullWidth
                                variant="text"
                                onClick={handleForgotPassword}
                                sx={{ mb: 1, color: 'primary.main' }}
                            >
                                {t('auth.forgotPassword')}
                            </Button>
                        )}

                        <Button fullWidth variant="text" onClick={handleBack}>
                            {t('auth.back')}
                        </Button>
                    </Box>
                )}

                {/* Step 3: Select OTP Channel */}
                {step === 'otp-channel' && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('auth.verifyNumber')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('auth.otpViaSMS')}
                        </Typography>

                        {captchaRequired && (
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {t('auth.tooManyAttempts')}
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 1 }}>{captchaQuestion} = ?</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('auth.yourAnswer')}
                                    value={captchaInput}
                                    onChange={(e) => setCaptchaInput(e.target.value.replace(/\D/g, ''))}
                                    inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                                />
                            </Box>
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleSendOTP}
                            disabled={loading || (captchaRequired && !captchaInput)}
                            sx={{ mb: 2 }}
                        >
                            {loading ? <CircularProgress size={24} /> : t('auth.sendOTP')}
                        </Button>

                        <Button fullWidth variant="text" onClick={handleBack}>
                            {t('auth.back')}
                        </Button>
                    </Box>
                )}

                {/* Step 4: Verify OTP and Set Password */}
                {step === 'otp-verify' && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {isNewUser ? t('auth.createAccount') : t('auth.resetPassword')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {isNewUser
                                ? t('auth.newUserOTP', { mobile })
                                : t('auth.existingUserOTP', { mobile })}
                        </Typography>

                        <TextField
                            fullWidth
                            label={t('auth.enterOTP')}
                            value={otp}
                            onChange={(e) => setOtp(formatMobile(e.target.value).slice(0, 6))}
                            type="text"
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            label={t('auth.createPassword')}
                            value={newPassword}
                            onChange={(e) => setNewPassword(formatMobile(e.target.value).slice(0, 6))}
                            type={showPassword ? 'text' : 'password'}
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />

                        {isNewUser && (
                            <TextField
                                fullWidth
                                label={t('auth.yourName')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, handleVerifyOTPAndSetPassword)}
                                placeholder="e.g. Raj Kumar"
                                inputProps={{ maxLength: 100 }}
                                sx={{ mb: 3 }}
                            />
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleVerifyOTPAndSetPassword}
                            disabled={loading || otp.length !== 6 || newPassword.length !== 6}
                            sx={{ mb: 2 }}
                        >
                            {loading ? <CircularProgress size={24} /> : t('auth.verifyAndContinue')}
                        </Button>

                        {captchaRequired && (
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {t('auth.tooManyResend')}
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 1 }}>{captchaQuestion} = ?</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('auth.yourAnswer')}
                                    value={captchaInput}
                                    onChange={(e) => setCaptchaInput(e.target.value.replace(/\D/g, ''))}
                                    inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                                />
                            </Box>
                        )}

                        <Button
                            fullWidth
                            variant="text"
                            onClick={handleSendOTP}
                            disabled={loading || (captchaRequired && !captchaInput)}
                        >
                            {t('auth.resendOTP')}
                        </Button>
                    </Box>
                )}
            </Paper>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {t('auth.termsAgreement')}{' '}
                    <Typography
                        component="span"
                        variant="body2"
                        color="primary"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setTermsOpen(true)}
                    >
                        {t('auth.termsLink')}
                    </Typography>
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                    {t('auth.copyright')}
                </Typography>
            </Box>

            <TermsAndConditionsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
        </Container>
    );
};

export default AuthPage;